import axiosInstance from "./axios";

// Moyasar Platform onboarding (system admin only): create our merchants as Moyasar
// sub-merchants, upload their KYC documents, submit for review and follow the status.
//
// Unlike the other API modules, every call here returns { ok, data, error } instead of
// toasting, because the pages need Moyasar's field errors ({ "info.id": [...] }) to put them
// under the matching input. The server never answers 401/403 for a Moyasar failure (that
// would log the admin out); it uses 422/502/503 with an errorCode.

const BASE = "/api/admin/moyasar-platform";

const fail = (error, fallback) => {
  const body = error?.response?.data;
  return {
    ok: false,
    data: null,
    error: {
      message: body?.message || error?.message || fallback,
      errorCode: body?.errorCode || null,
      fieldErrors: body?.data?.fieldErrors || null,
      status: error?.response?.status || null,
    },
  };
};

const call = async (fn, fallback) => {
  try {
    const res = await fn();
    if (res?.data && !res.data.isError) return { ok: true, data: res.data.data, error: null };
    return { ok: false, data: null, error: { message: res?.data?.message || fallback, errorCode: res?.data?.errorCode || null, fieldErrors: res?.data?.data?.fieldErrors || null } };
  } catch (error) {
    return fail(error, fallback);
  }
};

export const getPlatformInfo = () => call(() => axiosInstance.get(`${BASE}/platform`), "Could not load Moyasar settings");

export const listMoyasarMerchants = () => call(() => axiosInstance.get(`${BASE}/merchants`), "Could not load Moyasar merchants");

export const getMoyasarOnboarding = (merchantId) =>
  call(() => axiosInstance.get(`${BASE}/merchants/${merchantId}`), "Could not load this merchant's Moyasar onboarding");

export const createMoyasarMerchant = (merchantId, body) =>
  call(() => axiosInstance.post(`${BASE}/merchants/${merchantId}`, body), "Could not create the merchant on Moyasar");

export const linkMoyasarMerchant = (merchantId, moyasarMerchantId) =>
  call(() => axiosInstance.post(`${BASE}/merchants/${merchantId}/link`, { moyasarMerchantId }), "Could not link the Moyasar merchant");

export const updateMoyasarMerchant = (merchantId, body) =>
  call(() => axiosInstance.put(`${BASE}/merchants/${merchantId}`, body), "Could not update the merchant on Moyasar");

export const submitMoyasarReview = (merchantId) =>
  call(() => axiosInstance.post(`${BASE}/merchants/${merchantId}/review`), "Could not submit for review");

export const syncMoyasarMerchant = (merchantId) =>
  call(() => axiosInstance.post(`${BASE}/merchants/${merchantId}/sync`), "Could not refresh from Moyasar");

export const listMoyasarDocuments = (merchantId) =>
  call(() => axiosInstance.get(`${BASE}/merchants/${merchantId}/documents`), "Could not load documents");

export const uploadMoyasarDocument = (merchantId, { type, info, file }) => {
  const form = new FormData();
  form.append("type", type);
  form.append("info", JSON.stringify(info || {}));
  if (file) form.append("file", file);
  return call(() => axiosInstance.post(`${BASE}/merchants/${merchantId}/documents`, form, { rawRequest: true }), "Could not upload the document");
};

export const deleteMoyasarDocument = (merchantId, documentId) =>
  call(() => axiosInstance.delete(`${BASE}/merchants/${merchantId}/documents/${documentId}`), "Could not delete the document");

/** Fetch the file with the admin's token and hand it to the browser. */
export const downloadMoyasarDocument = async (merchantId, documentId) => {
  try {
    const res = await axiosInstance.get(`${BASE}/merchants/${merchantId}/documents/${documentId}/download`, { responseType: "blob" });
    const disposition = res.headers?.["content-disposition"] || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `document-${documentId}`;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return { ok: true, data: null, error: null };
  } catch (error) {
    return fail(error, "Could not download the document");
  }
};

// ─── Labels shared by the list and detail pages ─────────────────────────────

export const STATUS_INFO = {
  creating: { label: "Creating…", help: "The create request is in flight or Moyasar has not confirmed it yet. It is checked automatically." },
  create_failed: { label: "Create failed", help: "Moyasar rejected the details. Fix them and create again." },
  pending: { label: "Pending", help: "Created on Moyasar. Upload the required documents, then submit for review." },
  under_review: { label: "Under review", help: "Moyasar is reviewing. The Nafath signing link appears here once their checks pass." },
  missing_docs: { label: "Action needed", help: "Moyasar sent it back. Read the reasons and comment, fix, then submit again." },
  semi_active: { label: "Semi-active", help: "Payments work but settlements (payouts) are paused." },
  active: { label: "Active", help: "Fully active: payments and settlements." },
  hold: { label: "On hold", help: "Transfers held by Moyasar (fraud check or chargebacks)." },
  rejected: { label: "Rejected", help: "Moyasar will not serve this merchant." },
  disabled: { label: "Disabled", help: "Switched off on Moyasar (for example the contract signing was declined)." },
};

export const statusLabel = (status) => STATUS_INFO[status]?.label || status || "—";

// Moyasar's review reason codes, in words an admin can act on. Unknown codes show as-is.
export const REASON_LABELS = {
  app_under_development: "The website/app looks unfinished to the reviewer",
  app_not_working: "The website/app link did not work for the reviewer",
};

export const SIGNATURE_LABELS = {
  unsigned: "Not yet sent",
  initiated: "Waiting for the owner to sign on Nafath",
  signed: "Signed",
  rejected: "Declined by the signer",
};
